import { useSyncExternalStore } from 'react';
import { serverUrl } from './EsEnv';
import { API_HEALTH } from './EsConstants';

/**
 * Whether esback answers, as one fact the nav logo reads.
 *
 * <p>Every fetch goes through esFetch, which reports each outcome here, so a
 * failed read or write flips this at once and the next good one flips it back.
 * The poll exists for the idle page: nothing else would notice esback going
 * away, or coming back, while nobody clicks.
 *
 * <p><b>Down is a rejected fetch OR a gateway status.</b> esback is always
 * behind a proxy on this origin -- the ALB (502/503), compose's nginx (502), the
 * webpack dev server (504 on a refused connection) -- so esback going away
 * answers a status rather than rejecting. esback itself never sends those three,
 * so anything else, a 401 or a 404 included, is esback answering.
 */

const POLL_MS = 30000;

let down = false;
let poll = null;
const listeners = new Set();

function set( pDown ) {
    if ( down === pDown ) return;
    down = pDown;
    listeners.forEach( ( aListener ) => aListener() );
}

export function esServerAnswered( pResponse ) {
    set( [ 502, 503, 504 ].includes( pResponse?.status ) );
}

export function esServerUnreachable() {
    set( true );
}

async function ping() {
    try {
        esServerAnswered( await fetch( serverUrl + API_HEALTH, { credentials: 'include' } ) );
    }
    catch {
        esServerUnreachable();
    }
}

// The poll runs only while something is listening, so a module import (every
// jest file that touches esFetch) starts no timer.
function subscribe( pListener ) {
    listeners.add( pListener );
    if ( !poll ) poll = setInterval( ping, POLL_MS );
    return () => {
        listeners.delete( pListener );
        if ( listeners.size === 0 ) {
            clearInterval( poll );
            poll = null;
        }
    };
}

export function useEsServerDown() {
    return useSyncExternalStore( subscribe, () => down, () => false );
}
